import { History, ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useActionHistory } from '@/hooks/useActionHistory'
import { ActionHistoryFilters } from './ActionHistoryFilters'
import { ActionHistoryEntry } from './ActionHistoryEntry'

interface ActionHistoryProps {
  partyId: string
}

export function ActionHistory({ partyId }: ActionHistoryProps) {
  const {
    actions,
    totalCount,
    page,
    totalPages,
    isLoading,
    error,
    filters,
    setActionTypeFilter,
    setStatusFilter,
    setTickRange,
    setPage,
    resolveTargetName,
  } = useActionHistory(partyId)

  // Loading state
  if (isLoading && actions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Action History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 animate-pulse">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-12 bg-muted rounded" />
            <div className="h-12 bg-muted rounded" />
            <div className="h-12 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Action History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">Failed to load action history.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Action History
          </CardTitle>
          {totalCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {totalCount} action{totalCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Filters */}
        <ActionHistoryFilters
          actionType={filters.actionType}
          status={filters.status}
          tickFrom={filters.tickFrom}
          tickTo={filters.tickTo}
          onActionTypeChange={setActionTypeFilter}
          onStatusChange={setStatusFilter}
          onTickRangeChange={setTickRange}
        />

        {/* Action List */}
        {actions.length === 0 ? (
          <EmptyState hasFilters={!!filters.actionType || !!filters.status || filters.tickFrom !== undefined || filters.tickTo !== undefined} />
        ) : (
          <div className="space-y-2">
            {actions.map((action) => (
              <ActionHistoryEntry
                key={action.id}
                action={action}
                targetName={resolveTargetName(action)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  if (hasFilters) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground">
          No actions match your filters.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Try adjusting or clearing your filters.
        </p>
      </div>
    )
  }

  return (
    <div className="text-center py-8">
      <History className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
      <p className="text-sm font-medium mb-1">No actions taken yet</p>
      <p className="text-xs text-muted-foreground">
        Use the Actions Panel to start building your party!
      </p>
    </div>
  )
}
